import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button, InputPassword, FloatingField } from '../../components/aip';

// スタイルをインポート
import '../../styles/aip/index.css';

type PageState = 'loading' | 'valid' | 'invalid' | 'success' | 'error';

interface FieldErrors {
  password?: string;
  confirmPassword?: string;
}

/**
 * パスワードリセット実行ページ
 */
export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // トークンの有効性を確認
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setPageState('invalid');
        return;
      }

      try {
        const response = await fetch(`/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
        const data = await response.json();

        if (data.valid) {
          setEmail(data.email || '');
          setPageState('valid');
        } else {
          setPageState('invalid');
        }
      } catch {
        setPageState('invalid');
      }
    };

    validateToken();
  }, [token]);

  /**
   * フォームのバリデーション
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!password) {
      errors.password = 'パスワードを入力してください';
    } else if (password.length < 8) {
      errors.password = 'パスワードは8文字以上で入力してください';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'パスワード（確認）を入力してください';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'パスワードが一致しません';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length > 0;
  };

  /**
   * フィールドのエラーをクリア
   */
  const clearFieldError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPageState('success');
      } else {
        setError(data.message || 'パスワードの変更に失敗しました');
        if (response.status === 400) {
          setPageState('error');
        }
      }
    } catch {
      setError('通信エラーが発生しました。しばらく経ってから再度お試しください。');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (pageState) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        );

      case 'invalid':
        return (
          <div>
            <div
              style={{
                padding: '1.5rem',
                backgroundColor: 'var(--color-red50)',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ margin: 0, color: 'var(--color-alert)', fontWeight: 500 }}>
                無効なリンクです
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                このパスワードリセットリンクは無効か、有効期限が切れています。
                再度パスワードリセットをリクエストしてください。
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
              <Button
                as="a"
                href="/meeting/manage-new/password-reset"
                buttonType="solid-fill"
                size="md"
                style={{ width: '100%', maxWidth: '280px' }}
              >
                パスワードリセットを再リクエスト
              </Button>
              <Link to="/meeting/manage-new/login" className="aip-link">
                ログイン画面に戻る
              </Link>
            </div>
          </div>
        );

      case 'success':
        return (
          <div>
            <div
              style={{
                padding: '1.5rem',
                backgroundColor: 'var(--color-success)',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ margin: 0, color: '#ffffff', fontWeight: 500 }}>
                パスワードを変更しました
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                新しいパスワードでログインしてください。
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Button
                buttonType="solid-fill"
                size="md"
                onClick={() => navigate('/meeting/manage-new/login')}
                style={{ width: '100%', maxWidth: '280px' }}
              >
                ログイン画面へ
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div>
            <div
              style={{
                padding: '1.5rem',
                backgroundColor: 'var(--color-red50)',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <p style={{ margin: 0, color: 'var(--color-alert)', fontWeight: 500 }}>
                エラーが発生しました
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {error || 'パスワードの変更に失敗しました。再度お試しください。'}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}>
              <Button
                as="a"
                href="/meeting/manage-new/password-reset"
                buttonType="solid-fill"
                size="md"
                style={{ width: '100%', maxWidth: '280px' }}
              >
                パスワードリセットを再リクエスト
              </Button>
              <Link to="/meeting/manage-new/login" className="aip-link">
                ログイン画面に戻る
              </Link>
            </div>
          </div>
        );

      case 'valid':
      default:
        return (
          <>
            {email && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                <strong>{email}</strong> の新しいパスワードを入力してください。
              </p>
            )}

            {error && (
              <div
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--color-red50)',
                  color: 'var(--color-alert)',
                  borderRadius: '0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="aip-form-container__body">
                {/* 新しいパスワード */}
                <FloatingField
                  label="新しいパスワード"
                  htmlFor="password"
                  column="2"
                  required
                  supportText="8文字以上で入力してください"
                  errorText={fieldErrors.password}
                  hasError={!!fieldErrors.password}
                >
                  <InputPassword
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearFieldError('password');
                    }}
                    autoComplete="new-password"
                    aria-invalid={!!fieldErrors.password}
                  />
                </FloatingField>

                {/* パスワード確認 */}
                <FloatingField
                  label="パスワード（確認）"
                  htmlFor="confirmPassword"
                  column="2"
                  required
                  errorText={fieldErrors.confirmPassword}
                  hasError={!!fieldErrors.confirmPassword}
                >
                  <InputPassword
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      clearFieldError('confirmPassword');
                    }}
                    autoComplete="new-password"
                    aria-invalid={!!fieldErrors.confirmPassword}
                  />
                </FloatingField>

                {/* 送信ボタン */}
                <div data-column="2" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <Button
                    buttonType="solid-fill"
                    size="md"
                    type="submit"
                    disabled={isLoading || !password || !confirmPassword}
                    style={{ width: '100%' }}
                  >
                    {isLoading ? '変更中...' : 'パスワードを変更'}
                  </Button>

                  <div style={{ textAlign: 'center' }}>
                    <Link to="/meeting/manage-new/login" className="aip-link">
                      ログイン画面に戻る
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </>
        );
    }
  };

  return (
    <div className="aip-html-body">
      <div className="aip-main" data-size="xs">
        <header className="aip-main__header">
          <div className="aip-main__header-container">
            <h1 className="aip-main__header-title">AiiPo Management</h1>
          </div>
        </header>

        <div className="aip-main__content">
          <h2 style={{ fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' }}>
            パスワードの再設定
          </h2>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

